import useMeasureStore from '@/features/loads/components/general/store/useMeasureStore';
import { usePartyStore } from '@/features/parties/store';
import { IReport } from '@/features/reports/models';
import { formatDate } from '@/lib/format-date';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const createColumns = (): ColumnDef<IReport>[] => {
  const { t } = useTranslation();
  const { generationTypes, cropGenerations, cropTypes } = usePartyStore();
  const { harvestType } = useMeasureStore();

  return useMemo(
    () => [
      {
        accessorKey: 'deal_number',
        header: () => t('ticket.number'),
      },
      {
        accessorKey: 'number',
        header: () => t('account.number'),
      },
      {
        accessorKey: 'farmer_name',
        header: () => t('farm.name'),
      },
      {
        accessorKey: 'farmer_tax_number',
        header: () => t('stir.delivery'),
      },
      {
        accessorKey: 'place_name',
        header: () => t('place.name'),
      },
      {
        accessorKey: 'organization_name',
        header: () => t('claster'),
      },
      {
        accessorKey: 'organization_tax_number',
        header: () => t('organization.stir'),
      },
      {
        accessorKey: 'created_at',
        header: () => t('date.shipment.created'),
        cell: ({ row }) => formatDate(row?.original?.created_at),
      },
      {
        accessorKey: 'contract_type',
        header: () => t('contract.type'),
      },
      {
        accessorKey: 'weigh_in_date',
        header: () => t('incoming.measurement'),
        cell: ({ row }) => formatDate(row?.original?.weigh_in_date),
      },
      {
        accessorKey: 'weight_in',
        header: () => t('gross'),
      },
      {
        accessorKey: 'weigh_out_date',
        header: () => t('outgoing.weighing'),
        cell: ({ row }) => formatDate(row?.original?.weigh_out_date),
      },
      {
        accessorKey: 'weight_out',
        header: () => t('netto'),
      },
      {
        accessorKey: 'party_number',
        header: () => t('party.number'),
      },
      {
        accessorKey: 'place_number',
        header: () => t('place.number'),
      },
      {
        accessorKey: 'crop_type_name',
        header: () => t('selection.type.variety'),
      },

      {
        accessorKey: 'crop_generation_type',
        header: () => t('generation.type'),
        cell: ({ row }) =>
          generationTypes?.find(
            (v) => v?.value === row?.original?.crop_generation_type,
          )?.label ?? '-',
      },
      {
        accessorKey: 'crop_generation',
        header: () => t('generation'),
        cell: ({ row }) =>
          cropGenerations?.find(
            (v) => v?.value === row?.original?.crop_generation_id,
          )?.label ?? '-',
      },
      {
        accessorKey: 'harvest_type',
        header: () => t('technics.type'),
        cell: ({ row }) =>
          harvestType?.find((v) => v?.value === row?.original?.harvest_type)
            ?.label || '-',
      },
      {
        accessorKey: 'industrial_grade',
        header: () => t('industry.type'),
      },
      {
        accessorKey: 'class',
        header: () => t('class'),
      },
      {
        accessorKey: 'weight',
        header: () => t('actual.net.weight'),
        cell: ({ row }) => row?.original?.weight ?? '-',
      },
      {
        accessorKey: 'pollution',
        header: () => t('pollution2'),
        cell: ({ row }) => row?.original?.pollution ?? '-',
      },
      {
        accessorKey: 'weight_calc',
        header: () => t('calculated.weight2'),
        cell: ({ row }) => row?.original?.weight_calc ?? '-',
      },
      {
        accessorKey: 'humidity',
        header: () => t('humidity2'),
        cell: ({ row }) => row?.original?.humidity ?? '-',
      },
      {
        accessorKey: 'weight_cond',
        header: () => t('cond.weight'),
        cell: ({ row }) => row?.original?.weight_cond ?? '-',
      },
    ],
    [cropGenerations, harvestType, cropTypes, generationTypes],
  );
};
